import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowLeftIcon, HandshakeIcon, FilePdfIcon, EyeIcon, PrinterIcon } from "@phosphor-icons/react";
import EquipmentSelector from "@/components/EquipmentSelector";
import PhotoUploader from "@/components/PhotoUploader";
import AIDescriptionButton from "@/components/AIDescriptionButton";
import ReservationModal from "@/components/ReservationModal";
import InterestsPanel from "@/components/InterestsPanel";
import EnglishVersionPanel from "@/components/EnglishVersionPanel";
import CarGrExportPanel from "@/components/CarGrExportPanel";
import { Field, TextInput, NumberInput, Select, FUEL_TYPES, TRANSMISSIONS, BODY_TYPES, DRIVE_TYPES, COLORS, EXTERIOR_COLOR_TYPES, INTERIOR_TYPES, NUMPLATES, OWNERS_OPTIONS } from "@/components/CarFormFields";
import { CarGrMakeCombobox, CarGrModelCombobox } from "@/components/CarGrBrandSelect";
import { CargrListingDialog } from "@/components/CargrListingDialog";

const STATUS_OPTIONS = [
  { value: "available", label: "Διαθέσιμο" },
  { value: "reserved", label: "Κρατημένο" },
  { value: "sold", label: "Πωλήθηκε" },
];
const COLOR_OPTIONS = ["", ...COLORS];
const EXTERIOR_COLOR_TYPE_OPTIONS = ["", ...EXTERIOR_COLOR_TYPES];
const INTERIOR_TYPE_OPTIONS = ["", ...INTERIOR_TYPES];

export default function CarDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState(null);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showReservation, setShowReservation] = useState(false);
  const [showCargrDialog, setShowCargrDialog] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, b] = await Promise.all([api.get(`/cars/${id}`), api.get("/catalog/brands")]);
        setForm(c.data);
        setBrands(b.data.brands);
      } catch {
        toast.error("Δεν βρέθηκε");
        nav("/inventory");
      }
    })();
  }, [id, nav]);

  useEffect(() => {
    if (form?.brand) {
      api.get("/catalog/models", { params: { brand: form.brand } }).then((r) => setModels(r.data.models));
    }
  }, [form?.brand]);

  if (!form) return <div className="text-sm text-[#4B5563]">Φόρτωση...</div>;

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      ["year", "mileage_km", "price_eur", "engine_cc", "horsepower", "doors", "seats", "co2_emissions", "registration_month"].forEach((k) => {
        payload[k] = Number(payload[k]) || 0;
      });
      const r = await api.put(`/cars/${id}`, payload);
      setForm(r.data);
      if (r.data.wp_sync_triggered) toast.success("Αποθηκεύτηκε — το site ενημερώθηκε άμεσα");
      else toast.success("Αποθηκεύτηκε");
      if (r.data.ai_regen_started) {
        toast.info("Η περιγραφή ξαναδημιουργείται αυτόματα με τα νέα στοιχεία (~40 δευτ.)", { duration: 8000 });
        setTimeout(async () => {
          try {
            const rr = await api.get(`/cars/${id}`);
            setForm(rr.data);
            toast.success("Η νέα περιγραφή είναι έτοιμη");
          } catch { /* ignore */ }
        }, 45000);
      }
    } catch {
      toast.error("Αποτυχία");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="car-detail-page">
      <div className="flex items-center justify-between">
        <Link to="/inventory" className="text-sm flex items-center gap-2 hover:text-[#E60000]">
          <ArrowLeftIcon size={16} /> Πίσω στον στόλο
        </Link>
        <div className="flex items-center gap-2">
          {form.include_in_wp_sync !== false ? (
            <a
              href={`${API}/go/${form.id}`}
              target="_blank"
              rel="noreferrer"
              className="bg-[#0055FF] hover:bg-[#0044CC] text-white px-3 py-1.5 flex items-center gap-1.5 text-xs"
              data-testid="open-presentation"
            >
              <EyeIcon size={14} /> Προβολή στο site
            </a>
          ) : (
            <span
              className="bg-black/10 text-black/40 px-3 py-1.5 flex items-center gap-1.5 text-xs cursor-not-allowed"
              title="Δεν εμφανίζεται στο site"
              data-testid="open-presentation-disabled"
            >
              <EyeIcon size={14} /> Προβολή στο site
            </span>
          )}
          {form.include_in_car_gr_export !== false && form.cargr_listing_id ? (
            <a
              href={`https://www.car.gr/cars/view/${form.cargr_listing_id}`}
              target="_blank"
              rel="noreferrer"
              className="border border-[#0055FF] text-[#0055FF] hover:bg-[#0055FF] hover:text-white px-3 py-1.5 flex items-center gap-1.5 text-xs"
              data-testid="open-cargr"
            >
              <EyeIcon size={14} weight="fill" /> Προβολή στο car.gr
            </a>
          ) : form.include_in_car_gr_export !== false ? (
            <button
              type="button"
              onClick={() => setShowCargrDialog(true)}
              className="border border-[#0055FF]/30 text-[#0055FF]/50 hover:border-[#0055FF] hover:text-[#0055FF] px-3 py-1.5 flex items-center gap-1.5 text-xs"
              title="Λείπει ο αριθμός αγγελίας car.gr — πατήστε για συμπλήρωση"
              data-testid="set-cargr"
            >
              <EyeIcon size={14} weight="fill" /> Προβολή στο car.gr
            </button>
          ) : (
            <span
              className="border border-black/10 text-black/30 px-3 py-1.5 flex items-center gap-1.5 text-xs cursor-not-allowed"
              title="Δεν εξάγεται στο car.gr"
              data-testid="open-cargr-disabled"
            >
              <EyeIcon size={14} weight="fill" /> Προβολή στο car.gr
            </span>
          )}
          <a
            href={`${API}/cars/${form.id}/brochure.pdf`}
            target="_blank"
            rel="noreferrer"
            className="bg-[#16A34A] hover:bg-[#15803D] text-white px-3 py-1.5 flex items-center gap-1.5 text-xs"
            data-testid="open-brochure"
          >
            <PrinterIcon size={14} /> Brochure A4
          </a>
          <div className="font-mono text-xs text-[#4B5563] ml-2">ID: {form.id.slice(0, 8)}</div>
        </div>
      </div>

      <div className="border border-black/10 bg-[#0A0A0A] text-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/60">Καταχώρηση</div>
            <h2 className="heading text-3xl font-extrabold tracking-tight mt-1">
              {form.brand} {form.model} {form.variant && <span className="text-white/60">· {form.variant}</span>}
            </h2>
            <div className="mt-2 font-mono text-sm text-white/70">
              {form.year} · {new Intl.NumberFormat("el-GR").format(form.mileage_km)} χλμ. · {form.fuel_type}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowReservation(true)}
            className="bg-[#374151] hover:bg-[#1F2937] text-white px-4 py-2 flex items-center gap-1.5 text-sm font-medium"
            data-testid="reservation-btn"
          >
            <HandshakeIcon size={16} weight="bold" />
            {form.status === "available" ? "Κράτηση / Πώληση" : "Επεξεργασία Κράτησης"}
          </button>
        </div>
      </div>

      {showReservation && (
        <ReservationModal
          car={form}
          onClose={() => setShowReservation(false)}
          onSaved={(updated) => {
            setForm(updated);
            setShowReservation(false);
          }}
        />
      )}

      {showCargrDialog && (
        <CargrListingDialog
          car={form}
          onClose={() => setShowCargrDialog(false)}
          onSaved={(_, lid) => setForm((f) => ({ ...f, cargr_listing_id: lid }))}
        />
      )}

      <form onSubmit={submit} className="space-y-6">
        <Section title="Βασικά Στοιχεία" code="01">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Μάρκα" required>
              <CarGrMakeCombobox
                value={form.brand}
                makeId={form.car_gr_make_id}
                onPick={({ brand_name, make_id }) =>
                  setForm((s) => ({ ...s, brand: brand_name, car_gr_make_id: make_id, model: "", car_gr_model_id: 0 }))
                }
                dataTestid="form-brand"
              />
            </Field>
            <Field label="Μοντέλο" required>
              <CarGrModelCombobox
                value={form.model}
                modelId={form.car_gr_model_id}
                makeId={form.car_gr_make_id}
                onPick={({ model_name, model_id }) =>
                  setForm((s) => ({ ...s, model: model_name, car_gr_model_id: model_id }))
                }
                dataTestid="form-model"
              />
            </Field>
            <Field label="Έκδοση" hint={`${(form.variant || "").length}/60 χαρακτήρες (όριο car.gr)`}>
              <TextInput value={form.variant || ""} maxLength={60} onChange={(e) => set("variant", e.target.value)} />
            </Field>
            <Field label="Έτος" required>
              <NumberInput value={form.year} onChange={(e) => set("year", e.target.value)} />
            </Field>
            <Field label="Μήνας ταξινόμησης">
              <NumberInput value={form.registration_month} onChange={(e) => set("registration_month", e.target.value)} min={1} max={12} />
            </Field>
            <Field label="Χιλιόμετρα" required>
              <NumberInput value={form.mileage_km} onChange={(e) => set("mileage_km", e.target.value)} />
            </Field>
            <Field label="Τιμή (€)" required>
              <NumberInput value={form.price_eur} onChange={(e) => set("price_eur", e.target.value)} step={50} />
            </Field>
            <Field label="Κατάσταση">
              <Select
                value={form.status}
                onChange={(e) => set("status", e.target.value)}
                options={STATUS_OPTIONS}
              />
            </Field>
            <Field label="Προβεβλημένο">
              <label className="flex items-center gap-2 px-3 py-2 border border-black/20 bg-white cursor-pointer">
                <input type="checkbox" checked={!!form.featured} onChange={(e) => set("featured", e.target.checked)} />
                <span className="text-sm">Featured</span>
              </label>
            </Field>
            <Field label="Υπογραφή" hint="Η υπογραφή από τις Ρυθμίσεις δεν θα προστίθεται σε site/car.gr">
              <label className="flex items-center gap-2 px-3 py-2 border border-black/20 bg-white cursor-pointer">
                <input type="checkbox" checked={!!form.exclude_signature} onChange={(e) => set("exclude_signature", e.target.checked)} data-testid="exclude-signature-toggle" />
                <span className="text-sm">Χωρίς αυτόματη υπογραφή</span>
              </label>
            </Field>
            <Field label="Αριθμός αγγελίας car.gr" hint="Επικολλήστε link ή αριθμό — κενό = χωρίς σύνδεση">
              <TextInput
                value={form.cargr_listing_id || ""}
                onChange={(e) => {
                  const v = e.target.value;
                  const m = v.match(/\/view\/(\d+)/);
                  set("cargr_listing_id", m ? m[1] : v.replace(/[^\d]/g, ""));
                }}
                placeholder="π.χ. 53024901"
                data-testid="form-cargr-listing-id"
              />
            </Field>
            <Field label="Υποχρεωτικά στην περιγραφή" hint="Θα αναφερθούν οπωσδήποτε στην περιγραφή">
              <TextInput
                value={form.description_must_include || ""}
                onChange={(e) => set("description_must_include", e.target.value)}
                placeholder="π.χ. ελληνικής αντιπροσωπείας, πρώτο χέρι"
                data-testid="form-must-include"
              />
            </Field>
          </div>
        </Section>

        <Section title="Τεχνικά Χαρακτηριστικά" code="02">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Καύσιμο"><Select value={form.fuel_type} onChange={(e) => set("fuel_type", e.target.value)} options={FUEL_TYPES} /></Field>
            <Field label="Κιβώτιο"><Select value={form.transmission} onChange={(e) => set("transmission", e.target.value)} options={TRANSMISSIONS} /></Field>
            <Field label="Αμάξωμα"><Select value={form.body_type || ""} onChange={(e) => set("body_type", e.target.value)} options={BODY_TYPES} /></Field>
            <Field label="Κυβισμός (cc)"><NumberInput value={form.engine_cc || 0} onChange={(e) => set("engine_cc", e.target.value)} /></Field>
            <Field label="Ίπποι"><NumberInput value={form.horsepower || 0} onChange={(e) => set("horsepower", e.target.value)} /></Field>
            <Field label="CO₂"><NumberInput value={form.co2_emissions || 0} onChange={(e) => set("co2_emissions", e.target.value)} /></Field>
            <Field label="Θύρες"><NumberInput value={form.doors || 5} onChange={(e) => set("doors", e.target.value)} /></Field>
            <Field label="Θέσεις"><NumberInput value={form.seats || 5} onChange={(e) => set("seats", e.target.value)} /></Field>
            <Field label="Μετάδοση"><Select value={form.drive_type || "FWD"} onChange={(e) => set("drive_type", e.target.value)} options={DRIVE_TYPES} /></Field>
            <Field label="Εξωτερικό χρώμα"><Select value={form.color_exterior || ""} onChange={(e) => set("color_exterior", e.target.value)} options={COLOR_OPTIONS} /></Field>
            <Field label="Τύπος βαφής">
              <Select
                value={form.car_gr_exterior_color_type || ""}
                onChange={(e) => set("car_gr_exterior_color_type", e.target.value)}
                options={EXTERIOR_COLOR_TYPE_OPTIONS}
              />
            </Field>
            <Field label="Εσωτερικό χρώμα"><Select value={form.color_interior || ""} onChange={(e) => set("color_interior", e.target.value)} options={COLOR_OPTIONS} /></Field>
            <Field label="Τύπος ταπετσαρίας">
              <Select
                value={form.car_gr_interior_type || ""}
                onChange={(e) => set("car_gr_interior_type", e.target.value)}
                options={INTERIOR_TYPE_OPTIONS}
              />
            </Field>
            <Field label="Τοποθεσία"><TextInput value={form.location || ""} onChange={(e) => set("location", e.target.value)} /></Field>
            <Field label="Πινακίδες (κυκλοφορία)">
              <Select
                value={form.car_gr_numplate || ""}
                onChange={(e) => set("car_gr_numplate", e.target.value)}
                options={NUMPLATES}
              />
            </Field>
            <Field label="Προηγούμενοι ιδιοκτήτες" hint="Στέλνεται σε site/car.gr μόνο αν επιλέξετε αριθμό">
              <Select
                data-testid="prev-owners-select"
                value={String(form.car_gr_previous_owners || 0)}
                onChange={(e) => set("car_gr_previous_owners", Number(e.target.value) || 0)}
                options={OWNERS_OPTIONS}
              />
            </Field>
            <Field label="YouTube URL (παρουσίαση)">
              <TextInput
                value={form.car_gr_youtube_url || ""}
                onChange={(e) => set("car_gr_youtube_url", e.target.value)}
                placeholder="https://youtube.com/watch?v=..."
              />
            </Field>
            <Field label="Τέλη κυκλοφορίας (€)">
              <NumberInput
                value={form.car_gr_circulation_tax || 0}
                onChange={(e) => set("car_gr_circulation_tax", Number(e.target.value) || 0)}
              />
            </Field>
          </div>
        </Section>

        <Section title="Ταυτότητα" code="03">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="VIN"><TextInput value={form.vin || ""} onChange={(e) => set("vin", e.target.value.toUpperCase())} className="font-mono" maxLength={17} /></Field>
            <Field label="Πινακίδες"><TextInput value={form.license_plate || ""} onChange={(e) => set("license_plate", e.target.value.toUpperCase())} className="font-mono" /></Field>
          </div>
        </Section>

        {isAdmin && (
        <Section title="Περιγραφή" code="04">
          <AIDescriptionButton
            form={form}
            onGenerated={(payload) => {
              if (!payload) return;
              if (payload.description !== undefined) set("description", payload.description || form.description);
              if (payload.excerpt) set("excerpt", payload.excerpt);
              if (payload.seo_title) set("seo_title", payload.seo_title);
              if (payload.seo_description) set("seo_description", payload.seo_description);
            }}
          />
          <textarea
            value={form.description || ""}
            onChange={(e) => set("description", e.target.value)}
            rows={8}
            className="w-full px-3 py-2 border border-black/20 bg-white outline-none focus:border-[#0055FF] text-sm"
          />
          <div className="grid grid-cols-1 mt-4">
            <Field label="Excerpt (Ελληνικά)">
              <textarea
                value={form.excerpt || ""}
                onChange={(e) => set("excerpt", e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-black/20 bg-white outline-none focus:border-[#0055FF] text-sm"
                placeholder="Σύντομη περίληψη 1-2 προτάσεων"
                data-testid="excerpt-input"
              />
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <Field label="SEO Title (Ελληνικά, max 60 χαρακτήρες)">
              <TextInput value={form.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} data-testid="seo-title-input" />
            </Field>
            <Field label="SEO Description (Ελληνικά, max 160 χαρακτήρες)">
              <TextInput value={form.seo_description || ""} onChange={(e) => set("seo_description", e.target.value)} data-testid="seo-desc-input" />
            </Field>
          </div>
        </Section>
        )}

        <Section title="Εξοπλισμός" code="05">
          <EquipmentSelector value={form.equipment || []} onChange={(v) => set("equipment", v)} />
        </Section>

        <Section title="Φωτογραφίες" code="06">
          <PhotoUploader carId={form.id} photos={form.photos || []} onChange={(v) => set("photos", v)} />
        </Section>

        <Section title="Αγγλική Έκδοση (Polylang)" code="07">
          <EnglishVersionPanel form={form} set={set} />
        </Section>

        <Section title="car.gr Export" code="08">
          <CarGrExportPanel form={form} set={set} />
        </Section>

        <Section title="Ενδιαφέρον πελατών" code="09">
          <InterestsPanel carId={form.id} />
        </Section>

        {(form.status_history && form.status_history.length > 0) && (
          <Section title="Ιστορικό Κατάστασης" code="10">
            <StatusHistory items={form.status_history} />
          </Section>
        )}

        <div className="flex gap-3 justify-end border-t border-black/20 pt-6">
          <Link to="/inventory" className="btn-secondary">Πίσω</Link>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="update-car-btn">
            {saving ? "Αποθήκευση..." : "Αποθήκευση αλλαγών"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, code, children }) {
  return (
    <section className="border border-black/10 bg-white">
      <header className="px-6 py-4 border-b border-black/10 bg-[#F9FAFB] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="font-mono text-xs text-[#4B5563]">// {code}</span>
          <h3 className="heading font-bold text-base">{title}</h3>
        </div>
      </header>
      <div className="p-6">{children}</div>
    </section>
  );
}

function StatusHistory({ items }) {
  const labels = { available: "Διαθέσιμο", reserved: "Κρατημένο", sold: "Πωλημένο" };
  const sorted = [...items].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  return (
    <div className="space-y-2" data-testid="status-history">
      {sorted.map((h, i) => (
        <div key={h.id || i} className="border-l-2 border-[#0A0A0A] pl-3 py-1">
          <div className="text-sm flex flex-wrap items-center gap-2">
            <Badge label={labels[h.from_status] || h.from_status} muted />
            <span className="text-[#4B5563]">→</span>
            <Badge label={labels[h.to_status] || h.to_status} />
            <span className="text-xs font-mono text-[#4B5563]">
              · {new Date(h.created_at).toLocaleString("el-GR")} · {h.created_by || "—"}
            </span>
          </div>
          {h.note && (
            <div className="mt-1 text-sm text-[#0A0A0A] bg-[#FEF3C7] border-l-2 border-[#F59E0B] px-2 py-1">
              {h.note}
            </div>
          )}
          {h.customer_snapshot && (
            <div className="mt-1 text-xs font-mono text-[#4B5563]">
              Πελάτης κατά την αλλαγή: {h.customer_snapshot.name} · {h.customer_snapshot.phone || "—"}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Badge({ label, muted }) {
  return (
    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider ${muted ? "bg-[#F9FAFB] text-[#4B5563] border border-black/10" : "bg-[#0A0A0A] text-white"}`}>
      {label}
    </span>
  );
}
